/**
 * LeadTimeline — pretty timeline rendering of activity_log for a lead.
 *
 * Reads GET /activity?entity_type=lead&entity_id=<lead_id>&limit=200 and
 * renders one row per event with date + hour, an action-specific icon, and
 * a compact metadata line where useful. No new collection — purely a UI
 * layer over the canonical activity_log.
 *
 * PR #9.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Plus, Note, ArrowsClockwise, ChatCircle, EnvelopeSimple, PhoneCall,
  CheckCircle, XCircle, Repeat, Funnel, Tag, Globe, FileText, Sparkle,
} from "@phosphor-icons/react";

const ACTION_META = {
  "lead.created":                            { label: "Lead creado",            icon: Plus,         tone: "emerald" },
  "lead.status_changed":                     { label: "Cambio de etapa",        icon: ArrowsClockwise, tone: "blue" },
  "lead.assigned":                           { label: "Responsable asignado",   icon: Tag,          tone: "stone" },
  "lead.next_action_updated":                { label: "Próxima acción",         icon: Funnel,       tone: "stone" },
  "lead.meeting_updated":                    { label: "Call/reunión actualizada", icon: PhoneCall,  tone: "cyan" },
  "lead.created_note":                       { label: "Nota añadida",           icon: Note,         tone: "violet" },
  "lead.message_sent":                       { label: "Mensaje enviado",        icon: ChatCircle,   tone: "blue" },
  "lead.checklist_toggled":                  { label: "Checklist actualizada",  icon: CheckCircle,  tone: "stone" },
  "lead.dossier_sent":                       { label: "Dossier marcado enviado",icon: FileText,     tone: "violet" },
  "lead.contact_channel_updated":            { label: "Canal de contacto actualizado", icon: ChatCircle, tone: "blue" },
  "lead.preferences_updated":                { label: "Preferencias actualizadas", icon: Sparkle,   tone: "amber" },
  "lead.archived":                           { label: "Lead archivado",         icon: XCircle,      tone: "stone" },
  "lead.unarchived":                         { label: "Lead reactivado",        icon: Plus,         tone: "stone" },
  "lead.converted":                          { label: "Lead convertido a inversor", icon: CheckCircle, tone: "emerald" },
  "lead.investor_referral":                  { label: "Referido por inversor",  icon: Repeat,       tone: "amber" },
  "lead.investor_referral_duplicate":        { label: "Referido repetido",      icon: Repeat,       tone: "amber" },
  "lead.duplicate_submission":               { label: "Envío repetido (público)", icon: Repeat,     tone: "amber" },
  "automation.lead_created_notification.ok":     { label: "Email de aviso enviado", icon: EnvelopeSimple, tone: "emerald" },
  "automation.lead_created_notification.failed": { label: "Email de aviso fallido", icon: EnvelopeSimple, tone: "red" },
  "automation.lead_created_notification.skipped":{ label: "Email de aviso omitido", icon: EnvelopeSimple, tone: "stone" },
  "automation.lead_duplicate_notification.ok":     { label: "Email de duplicado enviado", icon: EnvelopeSimple, tone: "emerald" },
  "automation.lead_duplicate_notification.failed": { label: "Email de duplicado fallido", icon: EnvelopeSimple, tone: "red" },
  "automation.lead_duplicate_notification.skipped":{ label: "Email de duplicado omitido", icon: EnvelopeSimple, tone: "stone" },
  "automation.lead_created_webhook.ok":          { label: "Webhook enviado",        icon: Globe, tone: "emerald" },
  "automation.lead_created_webhook.failed":      { label: "Webhook fallido",        icon: Globe, tone: "red" },
  "automation.lead_duplicate_webhook.ok":        { label: "Webhook duplicado enviado", icon: Globe, tone: "emerald" },
  "automation.lead_duplicate_webhook.failed":    { label: "Webhook duplicado fallido", icon: Globe, tone: "red" },
  "lead.template_action.opened":             { label: "Plantilla abierta",       icon: ChatCircle,   tone: "stone" },
  // WhatsApp (Phase A/B) — metadata-only rows: the activity meta carries
  // message_type/origin but never message text, by design.
  "lead.whatsapp_inbound":                   { label: "WhatsApp recibido",       icon: ChatCircle,   tone: "emerald" },
  "lead.whatsapp_outbound":                  { label: "WhatsApp enviado",        icon: ChatCircle,   tone: "blue" },
  "lead.followup_paused_by_reply":           { label: "Seguimiento automático pausado por respuesta", icon: Funnel, tone: "amber" },
  "lead.reactivated_by_whatsapp":            { label: "Lead reactivado por WhatsApp", icon: Plus,    tone: "emerald" },
};

const TONE_BG = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  red:     "bg-red-50 border-red-200 text-red-700",
  amber:   "bg-amber-50 border-amber-200 text-amber-800",
  blue:    "bg-blue-50 border-blue-200 text-blue-700",
  cyan:    "bg-cyan-50 border-cyan-200 text-cyan-700",
  violet:  "bg-violet-50 border-violet-200 text-violet-700",
  stone:   "bg-stone-100 border-stone-200 text-stone-600",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function metaPreview(action, meta) {
  if (!meta || typeof meta !== "object") return null;
  if (action === "lead.status_changed") {
    return `${meta.from || "—"} → ${meta.to || "—"}`;
  }
  if (action === "lead.created_note") {
    const kind = meta.kind ? `[${meta.kind}] ` : "";
    return `${kind}${meta.preview || ""}`;
  }
  if (action === "lead.message_sent") {
    return `${meta.channel || ""} · ${meta.template_key || ""}`;
  }
  if (action === "lead.whatsapp_inbound" || action === "lead.whatsapp_outbound") {
    // Metadata only — activity meta never carries message text.
    const bits = [];
    if (meta.message_type) bits.push(meta.message_type);
    if (meta.origin === "mobile_app") bits.push("desde WhatsApp Business");
    return bits.join(" · ") || null;
  }
  if (action === "lead.contact_channel_updated") {
    return Object.entries(meta).map(([k, v]) => `${k}: ${v}`).join(" · ");
  }
  if (action === "lead.preferences_updated") {
    const entries = Object.entries(meta).slice(0, 4);
    return entries.map(([k, v]) =>
      Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${v}`
    ).join(" · ");
  }
  if (action === "lead.assigned") {
    return meta.assignee_name || meta.assigned_to_user_id || "—";
  }
  if (action === "lead.next_action_updated") {
    return `${meta.next_action || "—"}${meta.next_action_date ? " · " + meta.next_action_date : ""}`;
  }
  if (action === "lead.meeting_updated") {
    return `${meta.meeting_state || "—"}${meta.meeting_at ? " · " + meta.meeting_at : ""}`;
  }
  if (action === "lead.dossier_sent") {
    return meta.auto_advanced_status ? "Etapa avanzada a Dossier enviado" : null;
  }
  if (action.startsWith("automation.")) {
    const bits = [];
    if (meta.outcome) bits.push(meta.outcome);
    if (meta.http_status) bits.push(`HTTP ${meta.http_status}`);
    if (meta.error) bits.push(meta.error.slice(0, 60));
    return bits.join(" · ") || null;
  }
  return null;
}

export default function LeadTimeline({ leadId, limit = 200, max = 100 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoading(true);
    api.get("/activity", { params: { entity_type: "lead", entity_id: leadId, limit } })
      .then(({ data }) => {
        if (cancelled) return;
        setRows(Array.isArray(data) ? data.slice(0, max) : []);
        setError("");
      })
      .catch(() => { if (!cancelled) { setRows([]); setError("No se pudo cargar la actividad."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId, limit, max]);

  if (loading) return <div className="text-xs text-stone-400">Cargando actividad…</div>;
  if (error)   return <div className="text-xs text-red-600">{error}</div>;
  if (!rows.length) return <div className="text-xs text-stone-400">Aún no hay actividad registrada.</div>;

  return (
    <ol className="space-y-2" data-testid="lead-timeline">
      {rows.map((row, i) => {
        const meta = ACTION_META[row.action] || { label: row.action, icon: ChatCircle, tone: "stone" };
        const Icon = meta.icon;
        const previewText = metaPreview(row.action, row.meta);
        return (
          <li
            key={row.activity_id || `${row.timestamp}-${i}`}
            className="flex items-start gap-3"
            data-testid={`lead-timeline-row-${i}`}
          >
            <div className={`shrink-0 mt-0.5 w-7 h-7 rounded-full border flex items-center justify-center ${TONE_BG[meta.tone] || TONE_BG.stone}`}>
              <Icon size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-medium text-stone-800">{meta.label}</span>
                <span className="text-[11px] text-stone-400">{fmtDateTime(row.timestamp)}</span>
              </div>
              {previewText && (
                <div className="text-xs text-stone-500 truncate" title={previewText}>{previewText}</div>
              )}
              {row.actor_name && (
                <div className="text-[11px] text-stone-400">{row.actor_name}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
